import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { catchError, EMPTY, finalize } from 'rxjs';
import { AppscriptsNumber } from '../../../shared/models/appscripts-user.model';
import {
  PhoneReport,
  PhoneReportConnectivityInfo,
  PhoneReportConnectivityIssueDetails,
  PhoneReportConnectivityIssueType,
  PhoneReportConnectivityStatus,
} from '../../../shared/models/phone-report.model';
import { AppscriptsUserRequestParams, AppscriptsUserService } from '../../../shared/services/appscripts-user.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { PhoneReportConnectivityUtils } from '../../../shared/services/phone-report-connectivity-utils';
import { PhoneReportService } from '../../../shared/services/phone-report.service';
import { PingWeightEnum } from '../../../shared/models/ping.model';

@Component({
  standalone: false,
  selector: 'app-phone-report-connectivity-info',
  templateUrl: './phone-report-connectivity-info.component.html',
  styleUrls: ['./phone-report-connectivity-info.component.scss'],
})
export class PhoneReportConnectivityInfoComponent {
  readonly info = input<{
    report: PhoneReport;
    connectivityInfo: PhoneReportConnectivityInfo;
    isAdmin: boolean;
  } | null>(null);

  readonly datePipe = inject(DatePipe);
  readonly appscriptsUserService = inject(AppscriptsUserService);
  readonly notificationService = inject(NotificationService);
  readonly phoneReportService = inject(PhoneReportService);

  readonly loading = signal<boolean>(false);
  readonly phoneInfo = signal<AppscriptsNumber | null>(null);
  readonly reportIssueLabel = computed(() => this.phoneReportService.issues[this.info()?.report.issue] || 'Unknown');

  pingInfoExpanded = false;

  constructor() {
    effect(() => {
      const report = this.info()?.report;
      const isAdmin = this.info()?.isAdmin;

      if (report?.phone && isAdmin) {
        this.fetchPhoneInfo(report?.phone);
      }
    });
  }

  fetchPhoneInfo(phoneNumber: string) {
    const requestParams = new AppscriptsUserRequestParams();
    requestParams.page = 0;
    requestParams.filter = {
      phoneNumbers: [phoneNumber] as any,
    };

    this.loading.set(true);

    this.appscriptsUserService
      .all(requestParams)
      .pipe(
        catchError(error => {
          this.notificationService.error({
            title: 'Phone info',
            message: 'An error occurred while loading the phone number info',
            serviceName: 'APPSCRIPTS',
            requestMessage: error.statusText,
            requestCode: error.status,
          });

          return EMPTY;
        }),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe(res => {
        this.phoneInfo.set(res.content[0]);
      });
  }

  readonly missingPings = computed(() => {
    return this.info().connectivityInfo.issues.filter(issue => issue.type === PhoneReportConnectivityIssueType.MISSING_PING);
  });
  readonly irregularPings = computed(() => {
    return this.info().connectivityInfo.issues.filter(issue => issue.type === PhoneReportConnectivityIssueType.IRREGULAR_INTERVAL);
  });
  readonly degradedSignalPings = computed(() =>
    this.info().connectivityInfo.issues.filter(issue => issue.type === PhoneReportConnectivityIssueType.DEGRADED_SIGNAL)
  );

  readonly degradedSignalRanges = computed(() => {
    const pings = this.degradedSignalPings();
    if (!pings.length) return [];

    const ranges: { start: Date; end: Date; status: string }[] = [];
    let rangeStart = pings[0].timestampCurrent;
    let rangeEnd = pings[0].timestampCurrent;
    let rangeStatus = pings[0].details;

    for (let i = 1; i < pings.length; i++) {
      const ping = pings[i];
      if (ping.details === rangeStatus) {
        rangeEnd = ping.timestampCurrent;
      } else {
        ranges.push({ start: rangeStart, end: rangeEnd, status: rangeStatus });
        rangeStart = ping.timestampCurrent;
        rangeEnd = ping.timestampCurrent;
        rangeStatus = ping.details;
      }
    }
    ranges.push({ start: rangeStart, end: rangeEnd, status: rangeStatus });
    return ranges;
  });

  getDegradedRangeDuration(start: Date, end: Date): string {
    return PhoneReportConnectivityUtils.formatGap(start.getTime(), end.getTime());
  }

  readonly connectivityStatus = computed(() => this.info()?.connectivityInfo?.status);
  readonly isCollectingConnectivity = computed(() => this.connectivityStatus() === PhoneReportConnectivityStatus.COLLECTING);

  readonly testTtlToDate = computed(() =>
    this.info().report.testTtl ? new Date(this.info().report.testCreatedAt.getTime() + this.info().report.testTtl * 1000) : null
  );

  getIrregularPingGap(ping: PhoneReportConnectivityIssueDetails) {
    return PhoneReportConnectivityUtils.formatGap(ping.timestampPrev.getTime(), ping.timestampCurrent.getTime());
  }

  formatDate(date: Date | string, format = 'MMM d, y, HH:mm:ss') {
    return this.datePipe.transform(date, format);
  }
}
